import PropTypes from 'prop-types';
import React, { useCallback, useRef, useState, useEffect } from 'react';
import styled from 'styled-components';

import ArrowRightLineIcon from 'components/lib/icons/arrow_right_line_icon';
import CloseLineIcon from 'components/lib/icons/close_line_icon';
import SparkleIcon from 'components/lib/icons/sparkle_icon';
import DataVisualization from 'components/reporting/looker_ai_data_visualization';
import { formatMessageContent } from 'components/reporting/looker_ai_message_formatter';

export const WELCOME_MESSAGE_CONTENT =
  'Hi! I can help you explore your reporting data. Try a suggestion below or ask your own question — include a time frame like “last week” for best results.';

const DEFAULT_QUESTIONS = [
  'Tell me about this data',
  'What questions could I ask to better understand this data?',
  'How many conversations did we handle last week?',
  'Show me conversations by channel for the past month',
];

const ALL_SUGGESTED_QUESTIONS = [
  ...DEFAULT_QUESTIONS,
  'What is our average handle time this week?',
  'How does this week compare to last week?',
  "What's our average first response time last month?",
  'What are peak support hours for the past 4 weeks?',
  'How many conversations were resolved this week?',
];

function getRandomQuestions(count = 3) {
  const shuffled = [...ALL_SUGGESTED_QUESTIONS];
  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(Math.random() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];
  }
  return shuffled.slice(0, count);
}

export default function LookerConversationalAIPanel({
  analyticsConversationId,
  getNextMessageId,
  isTyping,
  messages,
  onAskAboutData,
  onClose,
  onMessageSent,
  setAnalyticsConversationId,
  setIsTyping,
  setMessages,
}) {
  const [inputValue, setInputValue] = useState('');
  const [showSuggestions, setShowSuggestions] = useState(messages.length <= 1);
  const [suggestedQuestions, setSuggestedQuestions] = useState(DEFAULT_QUESTIONS);
  const messagesAreaRef = useRef(null);

  const handleMoreIdeas = useCallback(() => {
    setSuggestedQuestions(getRandomQuestions(3));
  }, []);

  useEffect(() => {
    if (messagesAreaRef.current) {
      messagesAreaRef.current.scrollTop = messagesAreaRef.current.scrollHeight;
    }
  }, [messages, isTyping]);

  const sendMessage = useCallback(
    question => {
      if (!question || isTyping) return;

      const userMessage = { id: getNextMessageId(), role: 'user', content: question };
      setMessages(prev => [...prev, userMessage]);
      setInputValue('');
      setIsTyping(true);
      setShowSuggestions(false);
      onMessageSent && onMessageSent({ messageLength: question.length, turnIndex: messages.length });

      const timezone = Intl.DateTimeFormat().resolvedOptions().timeZone;

      onAskAboutData({
        question,
        analyticsConversationId,
        timezone,
        onSuccess: response => {
          setAnalyticsConversationId(response.analyticsConversationId || null);
          setMessages(prev => [
            ...prev,
            {
              id: getNextMessageId(),
              role: 'assistant',
              content: response.answer,
              data: response.data,
            },
          ]);
          setIsTyping(false);
        },
        onError: () => {
          setMessages(prev => [
            ...prev,
            {
              id: getNextMessageId(),
              role: 'assistant',
              content: 'Something went wrong. Please try again.',
              isError: true,
            },
          ]);
          setIsTyping(false);
        },
      });
    },
    [
      isTyping,
      analyticsConversationId,
      messages.length,
      onAskAboutData,
      onMessageSent,
      getNextMessageId,
      setMessages,
      setAnalyticsConversationId,
      setIsTyping,
    ]
  );

  const handleSubmit = useCallback(() => {
    sendMessage(inputValue.trim());
  }, [inputValue, sendMessage]);

  const handleSuggestedQuestion = useCallback(
    question => {
      sendMessage(question);
    },
    [sendMessage]
  );

  const onKeyDown = useCallback(
    evt => {
      if (evt.key === 'Enter' && !evt.shiftKey) {
        evt.preventDefault();
        handleSubmit();
      }
    },
    [handleSubmit]
  );

  return (
    <StyledPanel data-aid="lookerConversationalAIPanel">
      <Header>
        <StyledHeaderIcon height="16" width="16" />
        <HeaderText>Ask AI About My Data</HeaderText>
        <CloseButton onClick={onClose} title="Back to reports" type="button">
          <StyledCloseIcon height="18" width="18" />
        </CloseButton>
      </Header>
      <MessagesArea ref={messagesAreaRef}>
        {messages.map(msg => (
          <MessageRow key={msg.id} role={msg.role}>
            {msg.role === 'assistant' && (
              <AssistantAvatar>
                <SparkleIcon height="12" width="12" />
              </AssistantAvatar>
            )}
            <MessageContent role={msg.role}>
              {msg.data && <DataVisualization data={msg.data} />}
              <MessageBubble $hasData={!!msg.data} $isError={!!msg.isError} role={msg.role}>
                {formatMessageContent(msg.content)}
              </MessageBubble>
            </MessageContent>
          </MessageRow>
        ))}
        {isTyping && (
          <MessageRow role="assistant">
            <AssistantAvatar>
              <SparkleIcon height="12" width="12" />
            </AssistantAvatar>
            <MessageBubble role="assistant">
              <TypingDots>
                <Dot style={{ animationDelay: '0s' }} />
                <Dot style={{ animationDelay: '0.2s' }} />
                <Dot style={{ animationDelay: '0.4s' }} />
              </TypingDots>
            </MessageBubble>
          </MessageRow>
        )}
      </MessagesArea>
      <FooterArea>
        {showSuggestions ? (
          <SuggestionsContainer>
            <SuggestedQuestions>
              {suggestedQuestions.map((question, index) => (
                <SuggestedQuestionButton
                  disabled={isTyping}
                  key={index}
                  onClick={() => handleSuggestedQuestion(question)}
                  type="button"
                >
                  {question}
                </SuggestedQuestionButton>
              ))}
            </SuggestedQuestions>
            <MoreIdeasLink onClick={handleMoreIdeas}>
              <i className="fa fa-refresh" /> More ideas
            </MoreIdeasLink>
          </SuggestionsContainer>
        ) : (
          <ShowSuggestionsLink onClick={() => setShowSuggestions(true)}>
            <i className="fa fa-lightbulb-o" /> Show suggestions
          </ShowSuggestionsLink>
        )}
        <InputRow>
          <StyledInput
            data-aid="lookerAIInput"
            onChange={e => setInputValue(e.target.value)}
            onKeyDown={onKeyDown}
            placeholder="Ask about your reports..."
            value={inputValue}
          />
          <SendButton
            data-aid="lookerAISendButton"
            disabled={!inputValue.trim() || isTyping}
            onClick={handleSubmit}
            type="button"
          >
            <StyledArrowIcon height="16" width="16" />
          </SendButton>
        </InputRow>
      </FooterArea>
    </StyledPanel>
  );
}

LookerConversationalAIPanel.propTypes = {
  analyticsConversationId: PropTypes.string,
  getNextMessageId: PropTypes.func.isRequired,
  isTyping: PropTypes.bool.isRequired,
  messages: PropTypes.array.isRequired,
  onAskAboutData: PropTypes.func.isRequired,
  onClose: PropTypes.func.isRequired,
  onMessageSent: PropTypes.func,
  setAnalyticsConversationId: PropTypes.func.isRequired,
  setIsTyping: PropTypes.func.isRequired,
  setMessages: PropTypes.func.isRequired,
};

const StyledPanel = styled.div`
  background-color: ${p => p.theme.colors.white};
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  height: 100%;
  min-width: 0;
  overflow: hidden;
`;

const Header = styled.div`
  align-items: center;
  border-bottom: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  gap: 8px;
  padding: 16px 24px;
`;

const StyledHeaderIcon = styled(SparkleIcon)`
  color: ${p => p.theme.colors.purple400};
  flex-shrink: 0;
`;

const HeaderText = styled.span`
  color: ${p => p.theme.colors.purple400};
  font-size: 16px;
  font-weight: ${p => p.theme.fontWeight.medium};
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.gray500};
  cursor: pointer;
  font-size: 18px;
  margin-left: auto;
  padding: 4px 8px;
  &:hover {
    color: ${p => p.theme.colors.gray700};
  }
`;

const MessagesArea = styled.div`
  align-items: center;
  display: flex;
  flex: 1 1 auto;
  flex-direction: column;
  gap: 12px;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 24px 16px;

  > * {
    max-width: 800px;
    width: 100%;
  }
`;

const MessageRow = styled.div`
  align-items: center;
  display: flex;
  flex-direction: ${p => (p.role === 'user' ? 'row-reverse' : 'row')};
  gap: 8px;
`;

const AssistantAvatar = styled.div`
  align-items: center;
  background-color: ${p => p.theme.colors.purple100};
  border-radius: 50%;
  color: ${p => p.theme.colors.purple400};
  display: flex;
  flex-shrink: 0;
  height: 32px;
  justify-content: center;
  width: 32px;
`;

const MessageBubble = styled.div`
  background-color: ${p => (p.role === 'user' ? p.theme.colors.gray200 : 'transparent')};
  border-radius: ${p => (p.role === 'user' ? '12px 0 12px 12px' : '0')};
  color: ${p => (p.$isError ? p.theme.colors.red400 : p.theme.colors.gray800)};
  font-size: 14px;
  line-height: 1.5;
  padding: ${p => (p.role === 'user' ? '10px 14px' : '0')};
  white-space: ${p => (p.role === 'user' ? 'pre-wrap' : 'normal')};
  ${p => !p.$hasData && 'max-width: 100%;'}
`;

const FooterArea = styled.div`
  align-items: center;
  background-color: ${p => p.theme.colors.white};
  border-top: 1px solid ${p => p.theme.colors.gray300};
  display: flex;
  flex-direction: column;
  flex-shrink: 0;
  padding: 16px 24px;

  > * {
    max-width: 800px;
    width: 100%;
  }
`;

const SuggestionsContainer = styled.div`
  margin-bottom: 12px;
`;

const SuggestedQuestions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
  margin-bottom: 8px;
`;

const MoreIdeasLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.purple400};
  cursor: pointer;
  font-size: 12px;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
  i {
    margin-right: 4px;
  }
`;

const SuggestedQuestionButton = styled.button`
  background-color: ${p => p.theme.colors.white};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 16px;
  color: ${p => p.theme.colors.gray700};
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  font-size: 13px;
  opacity: ${p => (p.disabled ? 0.5 : 1)};
  padding: 6px 12px;
  transition: all 0.15s ease;
  &:hover:not(:disabled) {
    background-color: ${p => p.theme.colors.purple50};
    border-color: ${p => p.theme.colors.purple400};
    color: ${p => p.theme.colors.purple400};
  }
`;

const InputRow = styled.div`
  align-items: center;
  display: flex;
  gap: 8px;
`;

const ShowSuggestionsLink = styled.button`
  background: none;
  border: none;
  color: ${p => p.theme.colors.purple400};
  cursor: pointer;
  font-size: 13px;
  margin-bottom: 12px;
  padding: 0;
  &:hover {
    text-decoration: underline;
  }
  i {
    margin-right: 4px;
  }
`;

const StyledInput = styled.input`
  background-color: ${p => p.theme.colors.gray100};
  border: 1px solid ${p => p.theme.colors.gray300};
  border-radius: 20px;
  flex: 1;
  font-size: 14px;
  outline: none;
  padding: 10px 16px;
  &:focus {
    border-color: ${p => p.theme.colors.purple400};
  }
  &::placeholder {
    color: ${p => p.theme.colors.gray400};
  }
`;

const SendButton = styled.button`
  align-items: center;
  background-color: ${p => (p.disabled ? p.theme.colors.gray300 : p.theme.colors.purple400)};
  border: none;
  border-radius: 50%;
  color: ${p => p.theme.colors.white};
  cursor: ${p => (p.disabled ? 'not-allowed' : 'pointer')};
  display: flex;
  height: 36px;
  justify-content: center;
  width: 36px;
  &:hover:not(:disabled) {
    background-color: ${p => p.theme.colors.purple500};
  }
`;

const StyledArrowIcon = styled(ArrowRightLineIcon)`
  color: inherit;
`;

const StyledCloseIcon = styled(CloseLineIcon)`
  color: inherit;
`;

const TypingDots = styled.div`
  display: flex;
  gap: 4px;
  padding: 4px 0;
`;

const Dot = styled.div`
  animation: pulse 1s ease-in-out infinite;
  background: ${p => p.theme.colors.gray400};
  border-radius: 50%;
  height: 6px;
  width: 6px;

  @keyframes pulse {
    0%,
    80%,
    100% {
      opacity: 0.3;
    }
    40% {
      opacity: 1;
    }
  }
`;

const MessageContent = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  max-width: 85%;
`;
